import { useEffect, useRef, useState } from 'react';

import { ActionIcon, Box, FileButton, Group, Loader, Stack, Text, Textarea } from '@mantine/core';
import { IconCheck, IconMinus, IconPaperclip, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ALLOWED_FILE_MIME_TYPES, type TurnProgress, type TurnRead } from '@teo/backend';
import { resolveSemanticColor, text } from '@teo/design-tokens';
import {
  type AgentAttachment,
  type AgentTurnOutcome,
  MAX_ATTACHMENTS_PER_QUESTION,
} from '@teo/types';

import { uploadAgentAttachment } from '../../lib/api';
import { errorMessageOf, readOf } from '../../lib/readOf';
import { trpc } from '../../lib/trpc';
import { StaleNotice } from '../common/StaleNotice';
import { AppButton } from '../design-system/AppButton';
import { InlineAlert } from '../design-system/InlineAlert';
import { AgentMarkdown } from './AgentMarkdown';

/** How often the panel asks where a turn has got to while the agent answers. */
const TURN_POLL_MS = 1_500;

/** Longer than a model step of a healthy day, which takes 2 to 12 seconds. */
const SLOW_MODEL_MS = 20_000;

/** How many reads the panel lists while the agent answers; older ones are counted. */
const READS_SHOWN = 5;

/** The file types the upload routes take. */
const ATTACHMENT_TYPES = ALLOWED_FILE_MIME_TYPES.join(',');

/** The files of one choice that uploaded, and why each of the others did not. */
type UploadResult = { added: AgentAttachment[]; failed: string[] };

/**
 * Upload each chosen file on its own, so one bad file does not hold back the
 * others. A choice where nothing uploaded fails as a whole.
 */
async function uploadEach(chosen: readonly File[]): Promise<UploadResult> {
  const settled = await Promise.allSettled(chosen.map(uploadAgentAttachment));
  const added = settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
  const failed = settled.flatMap((result) =>
    result.status === 'rejected' ? [errorMessageOf(result.reason)] : [],
  );
  if (added.length === 0) throw new Error(failed[0] ?? 'The files could not be attached.');
  return { added, failed };
}

/** A question on its way: what the person typed, and the files they attached. */
type PendingQuestion = { text: string; attachments: readonly AgentAttachment[] };

/** Why a turn stopped without an answer, for the person who asked. */
function describeOutcome(outcome: AgentTurnOutcome): string | undefined {
  switch (outcome.kind) {
    case 'replied':
      return undefined;
    case 'failed':
      return outcome.message;
    case 'gaveUp':
      switch (outcome.why.reason) {
        case 'exchangesSpent':
          return `The agent stopped after ${outcome.why.exchanges} exchanges without an answer.`;
        case 'stuck':
          return `The agent stopped: the same call was refused ${outcome.why.times} times (${outcome.why.repeatedError}).`;
        case 'silent':
          return 'The agent stopped without saying anything.';
        case 'chatFailed':
          // The error is the model provider's, and it means nothing to the person.
          return 'The agent timed out. Would you like to try again?';
        case 'outOfTime':
          return `The agent stopped after ${Math.round(outcome.why.elapsedMs / 60_000)} minutes without an answer.`;
      }
  }
}

/**
 * What to say when the model has worked on one step for a long time, so that a
 * slow model does not look like a stuck agent.
 */
function slowModelNotice(turn: TurnProgress | null | undefined): string | undefined {
  if (!turn || turn.status === 'finished') return undefined;
  return turn.modelWaitMs >= SLOW_MODEL_MS
    ? 'The model is slow to answer. The agent is still waiting for it.'
    : undefined;
}

/** Whether the agent is still working on a turn. */
function isAnswering(turn: TurnProgress | null | undefined): boolean {
  return turn?.status === 'queued' || turn?.status === 'running';
}

/** The reads of a turn the panel lists: the latest few, and how many came before them. */
function latestReads(turn: TurnProgress | null | undefined): {
  earlier: number;
  shown: readonly TurnRead[];
} {
  if (turn?.status !== 'queued' && turn?.status !== 'running') return { earlier: 0, shown: [] };
  const shown = turn.reads.slice(-READS_SHOWN);
  return { earlier: turn.reads.length - shown.length, shown };
}

function ReadIcon({ state }: { state: TurnRead['state'] }) {
  switch (state) {
    case 'running':
      return <Loader size={12} />;
    case 'done':
      return <IconCheck size={12} />;
    case 'failed':
      return <IconX size={12} />;
  }
}

/**
 * What the agent is doing while it answers: a line for each of its latest
 * reads. It stands in for the answer, so it goes when the answer comes.
 */
function TurnActivity({ turn }: { turn: TurnProgress | null | undefined }) {
  const secondary = { color: resolveSemanticColor(text.secondary) };
  const { earlier, shown } = latestReads(turn);
  const slow = slowModelNotice(turn);
  return (
    <Stack gap={2} data-testid="agent-turn-activity">
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="sm" style={secondary}>
          The agent is reading your data…
        </Text>
      </Group>
      {slow !== undefined && (
        <Text size="xs" pl="lg" style={secondary} data-testid="agent-slow-model">
          {slow}
        </Text>
      )}
      {earlier > 0 && (
        <Text size="xs" pl="lg" style={secondary}>
          {earlier === 1 ? '1 earlier read' : `${earlier} earlier reads`}
        </Text>
      )}
      {shown.map((read, index) => (
        <Group key={earlier + index} gap={6} pl="lg" wrap="nowrap" style={secondary}>
          <ReadIcon state={read.state} />
          <Text size="xs" lineClamp={1} title={read.summary} data-testid="agent-turn-read">
            {read.summary}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

/** The name of each file, as a line under the words of a question. */
function AttachmentLines({ attachments }: { attachments: readonly AgentAttachment[] }) {
  return (
    <Stack gap={2}>
      {attachments.map((attachment) => (
        <Group key={attachment.id} gap={4} wrap="nowrap" data-testid="agent-chat-attachment">
          <IconPaperclip size={12} />
          <Text size="xs" c="inherit" lineClamp={1}>
            {attachment.name}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

/**
 * One message, as a messaging app shows it: the person's on the right in the
 * brand purple, the agent's on the left in white. The agent writes Markdown;
 * the person's text is shown as typed, with the files they attached under it.
 */
function Bubble({
  from,
  children,
  attachments = [],
}: {
  from: 'person' | 'agent';
  children: string;
  attachments?: readonly AgentAttachment[] | undefined;
}) {
  const person = from === 'person';
  return (
    <Box
      data-testid={person ? 'agent-chat-question' : 'agent-chat-answer'}
      maw="85%"
      px="sm"
      py={6}
      style={{
        alignSelf: person ? 'flex-end' : 'flex-start',
        borderRadius: 16,
        borderBottomRightRadius: person ? 4 : 16,
        borderBottomLeftRadius: person ? 16 : 4,
        background: person ? 'var(--mantine-color-purple-5)' : 'var(--mantine-color-white)',
        color: person ? 'var(--mantine-color-white)' : 'var(--mantine-color-purple-7)',
        border: person ? undefined : '1px solid var(--mantine-color-purple-1)',
      }}
    >
      {person ? (
        <Stack gap={4}>
          {children !== '' && (
            <Text size="sm" c="inherit" style={{ whiteSpace: 'pre-wrap' }}>
              {children}
            </Text>
          )}
          {attachments.length > 0 && <AttachmentLines attachments={attachments} />}
        </Stack>
      ) : (
        <AgentMarkdown>{children}</AgentMarkdown>
      )}
    </Box>
  );
}

export type AgentChatProps = {
  /** The greeting names the person the agent acts as. */
  firstName: string;
  onMinimize: () => void;
};

/** Talk to the agent, which answers as the signed-in person. */
export function AgentChat({ firstName, onMinimize }: AgentChatProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<PendingQuestion | undefined>();
  const bottom = useRef<HTMLDivElement>(null);

  const conversationOptions = trpc.agentChat.getConversation.queryOptions();
  const conversation = useQuery(conversationOptions);
  const conversationRead = readOf(conversation);

  // Polled only while the agent works. A reload in the middle of a turn picks
  // it up again, because the turn lives on the server.
  const turnOptions = trpc.agentChat.latestTurn.queryOptions(undefined, {
    refetchInterval: (query) => (isAnswering(query.state.data) ? TURN_POLL_MS : false),
  });
  const turn = useQuery(turnOptions);
  const answering = isAnswering(turn.data);

  const refresh = () => queryClient.invalidateQueries({ queryKey: conversationOptions.queryKey });
  const refreshTurn = () => queryClient.invalidateQueries({ queryKey: turnOptions.queryKey });

  const send = useMutation(
    trpc.agentChat.send.mutationOptions({
      onSettled: async () => {
        // The question is in the conversation once it is queued.
        await Promise.all([refresh(), refreshTurn()]);
        setPending(undefined);
      },
    }),
  );

  const upload = useMutation({ mutationFn: uploadEach });
  const [notAttached, setNotAttached] = useState<string[]>([]);

  const reset = useMutation(
    trpc.agentChat.reset.mutationOptions({
      // A reset puts the last turn away, and its warning with it.
      onSuccess: async () => {
        await Promise.all([refresh(), refreshTurn()]);
      },
    }),
  );

  // The answer is in the conversation once the turn has finished.
  const finishedTurnId = turn.data?.status === 'finished' ? turn.data.turnId : undefined;
  useEffect(() => {
    if (finishedTurnId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: conversationOptions.queryKey });
    }
  }, [finishedTurnId, queryClient, conversationOptions.queryKey]);

  // A turn can change what the page shows, so the page reads again when a
  // turn it watched ends. A turn that had ended before the page loaded is
  // already in what the page read.
  const wasAnswering = useRef(false);
  useEffect(() => {
    if (wasAnswering.current && !answering) void queryClient.invalidateQueries();
    wasAnswering.current = answering;
  }, [answering, queryClient]);

  const messages = conversation.data ?? [];
  const { earlier, shown } = latestReads(turn.data);
  const readCount = earlier + shown.length;

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages.length, pending, readCount]);

  const busy = send.isPending || answering;

  /** Send a question with its files. Words that fail to send come back to the box. */
  const ask = (question: PendingQuestion) => {
    setPending(question);
    send.mutate(
      { text: question.text, attachmentIds: question.attachments.map(({ id }) => id) },
      { onError: () => setDraft(question.text) },
    );
  };

  const submit = () => {
    const question = draft.trim();
    if (question === '' || busy || upload.isPending) return;
    setDraft('');
    ask({ text: question, attachments: [] });
  };

  /**
   * A chosen file goes to the agent at once, with any words typed before it,
   * so the agent starts on the file and uses what the person said about it.
   * The files that uploaded go even when another did not, and the panel says
   * which did not.
   */
  const attach = (chosen: readonly File[]) => {
    if (chosen.length === 0 || busy) return;
    const question = draft.trim();
    setDraft('');
    setNotAttached([]);
    upload.mutate(chosen.slice(0, MAX_ATTACHMENTS_PER_QUESTION), {
      onSuccess: ({ added, failed }) => {
        setNotAttached(failed);
        ask({ text: question, attachments: added });
      },
      onError: () => setDraft(question),
    });
  };

  const stopped = turn.data?.status === 'finished' ? describeOutcome(turn.data.outcome) : undefined;
  const timedOut =
    turn.data?.status === 'finished' &&
    turn.data.outcome.kind === 'gaveUp' &&
    turn.data.outcome.why.reason === 'chatFailed';
  const lastQuestion = messages.filter((message) => message.from === 'person').at(-1);

  const askAgain = () => {
    if (lastQuestion === undefined || busy) return;
    ask({ text: lastQuestion.text, attachments: lastQuestion.attachments ?? [] });
  };

  return (
    <Stack gap={0} h="100%" data-testid="agent-chat">
      <Group justify="flex-end" wrap="nowrap" px="sm" py="xs">
        <Group gap={4} wrap="nowrap">
          <AppButton
            variant="subtle"
            intent="neutral"
            size="compact-xs"
            onClick={() => reset.mutate()}
            loading={reset.isPending}
            disabled={busy}
          >
            Start over
          </AppButton>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Minimize the agent"
            onClick={onMinimize}
          >
            <IconMinus size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <Stack gap="xs" px="sm" py="xs" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Bubble from="agent">
          {`Hi ${firstName}. Ask me about the data you can see, such as “Which invoices are open for my ownership?”, or ask me to make a change for you. You can also attach a file, such as a bill, and I will file it where you say.`}
        </Bubble>
        {conversation.isLoading && <Loader size="sm" />}
        {conversationRead.kind === 'failed' && (
          <InlineAlert intent="danger">{errorMessageOf(conversationRead.error)}</InlineAlert>
        )}
        {conversationRead.kind === 'ready' && conversationRead.refreshFailed && <StaleNotice />}

        {messages.map((message) => (
          <Bubble
            key={message.id}
            from={message.from}
            attachments={message.from === 'person' ? message.attachments : undefined}
          >
            {message.text}
          </Bubble>
        ))}

        {pending !== undefined && (
          <Bubble from="person" attachments={pending.attachments}>
            {pending.text}
          </Bubble>
        )}
        {(pending !== undefined || answering) && <TurnActivity turn={turn.data} />}

        {send.error && <InlineAlert intent="danger">{send.error.message}</InlineAlert>}
        {upload.error && <InlineAlert intent="danger">{upload.error.message}</InlineAlert>}
        {notAttached.map((reason) => (
          <InlineAlert key={reason} intent="warning">
            {reason}
          </InlineAlert>
        ))}
        {reset.error && <InlineAlert intent="danger">{reset.error.message}</InlineAlert>}
        {stopped !== undefined && <InlineAlert intent="warning">{stopped}</InlineAlert>}
        {timedOut && lastQuestion !== undefined && (
          <Group>
            <AppButton variant="light" intent="primary" size="xs" onClick={askAgain}>
              Try again
            </AppButton>
          </Group>
        )}
        <div ref={bottom} />
      </Stack>

      <Stack gap="xs" p="sm">
        <Textarea
          aria-label="Ask the agent"
          placeholder="Ask a question. Press Enter to send."
          autosize
          minRows={1}
          maxRows={6}
          value={draft}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          disabled={busy}
        />
        <Group justify="space-between">
          <FileButton
            multiple
            accept={ATTACHMENT_TYPES}
            onChange={attach}
            disabled={busy || upload.isPending}
          >
            {(props) => (
              <ActionIcon
                {...props}
                variant="subtle"
                color="gray"
                aria-label="Attach a file"
                loading={upload.isPending}
                disabled={busy || upload.isPending}
              >
                <IconPaperclip size={16} />
              </ActionIcon>
            )}
          </FileButton>
          <AppButton
            variant="filled"
            intent="primary"
            size="xs"
            onClick={submit}
            // The working line above already spins while the agent answers.
            disabled={busy || upload.isPending || draft.trim() === ''}
          >
            Send
          </AppButton>
        </Group>
      </Stack>
    </Stack>
  );
}

export const __private = { describeOutcome, isAnswering, latestReads, slowModelNotice };
